import { CONTRACT_STATUS_ACTIVE, CONTRACT_STATUS_CANCELLATION_SCHEDULED, CONTRACT_STATUS_CANCELLED, CONTRACT_STATUS_EXPIRED, CONTRACT_STATUS_INACTIVE, CONTRACT_STATUS_UPPER_ACTIVE, CONTRACT_STATUS_UPPER_CANCELED, CONTRACT_STATUS_UPPER_EXPIRED, CONTRACT_STATUS_UPPER_INACTIVE, FSM_CONTRACT_STATUS_SCHEDULED_CANCEL } from "../components/pages/my-plan/my-plan.constants";
import { STOP_BILL_FEATURE_ENABLED } from "./feature-flags";
import { IContractData, IPropertyAddress } from "../interfaces/IJob";

export const phoneReFormat = (number: string) => {
  if (number) return number.replace(/[^0-9]+/g, '');
  return '0';
};


export const parseJwt = (token: string) => {
  if (!token || typeof token !== 'string' || token.split('.').length < 2) {
    return {};
  }
  let base64Url = token.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  let jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  return JSON.parse(jsonPayload);
};


export function getPlanStatus(contractData: any): string {
  let status = 'Unknown';
  if (STOP_BILL_FEATURE_ENABLED && contractData?.baseContract.fsmContractStatus === FSM_CONTRACT_STATUS_SCHEDULED_CANCEL) {
    status = contractData?.baseContract.fsmContractStatus;
  } else {
    status = contractData?.baseContract?.contractStatus;
  }
  switch (status) {
    case CONTRACT_STATUS_UPPER_ACTIVE:
      return CONTRACT_STATUS_ACTIVE;
    case CONTRACT_STATUS_UPPER_INACTIVE:
      return CONTRACT_STATUS_INACTIVE;
    case CONTRACT_STATUS_UPPER_CANCELED:
      return CONTRACT_STATUS_CANCELLED;
    case CONTRACT_STATUS_UPPER_EXPIRED:
      return CONTRACT_STATUS_EXPIRED;
    case FSM_CONTRACT_STATUS_SCHEDULED_CANCEL:
      return CONTRACT_STATUS_CANCELLATION_SCHEDULED;
    default:
      return 'Unknown';
  }
}

export function getIconClass(planStatus: string): string {
  switch (planStatus) {
    case CONTRACT_STATUS_ACTIVE:
      return 'pi pi-check-circle text-emerald-900 text-xs';
    case CONTRACT_STATUS_CANCELLED:
      return 'pi pi-exclamation-circle text-slate-700 text-xs';
    case CONTRACT_STATUS_CANCELLATION_SCHEDULED:
      return '';
    default:
      return 'pi pi-exclamation-triangle text-orange-900 text-xs';
  }
}

export function getTextBgClass(planStatus: string): string {
  switch (planStatus) {
    case CONTRACT_STATUS_ACTIVE:
      return 'text-emerald-900 bg-emerald-100 border-emerald-200 font-normal text-sm md:text-base';
    case CONTRACT_STATUS_CANCELLED:
      return 'text-slate-700 bg-slate-100 border-slate-200 font-normal text-sm md:text-base';
    case CONTRACT_STATUS_CANCELLATION_SCHEDULED:
      return 'text-rose-900 bg-rose-100 border-rose-200 font-normal text-sm md:text-base';
    default:
      return 'text-orange-900 bg-orange-100 border-orange-200 font-normal text-sm md:text-base';
  }
}

export function trackByIndex(index: number): number {
  return index;
}

/**
 * Appends ' (Air Conditioner)' to any item whose name starts with 'Straight Cool'.
 * Resolves the display presentation issue for Air Conditioner service requests.
 *
 * @param item - An item object containing a name property
 * @returns A shallow cloned item with the appended suffix if matching, or the original item
 */
export function formatStraightCoolItemName<T extends { name?: string }>(item: T): T {
  if (item?.name && item.name.startsWith('Straight Cool')) {
    return { ...item, name: item.name + ' (Air Conditioner)' };
  }
  return item;
}

/**
 * Transforms a string with address-aware title-casing:
 * - Lowercases first, then capitalizes leading letters of words
 * - Preserves letter-digit alphanumeric units (e.g. 12A)
 */
export function capitalizeFirst(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/\b([a-z])/g, (char) => char.toUpperCase())
    .replace(/(\d+)([a-z])\b/gi, (_, num, char) => num + char.toUpperCase())
}

/**
 * Normalizes an address object in-place so its lines and city are title-cased
 * while preserving state and zip.
 */
export function normalizeAddress<T extends IPropertyAddress | null | undefined>(address: T): T {
  if (!address) return address;
  if (address.addressLine1) {
    address.addressLine1 = capitalizeFirst(address.addressLine1);
  }
  if (address.addressLine2) {
    address.addressLine2 = capitalizeFirst(address.addressLine2);
  }
  if (address.addressLine3) {
    address.addressLine3 = capitalizeFirst(address.addressLine3);
  }
  if (address.city) {
    address.city = capitalizeFirst(address.city);
  }
  return address;
}

/**
 * Normalizes all address fields within a contract data object in-place.
 */
export function normalizeContractAddresses<T extends IContractData | null | undefined>(contract: T): T {
  if (!contract) return contract;
  if (contract.baseContract?.propertyAddress) {
    normalizeAddress(contract.baseContract.propertyAddress);
  }
  if (contract.renewalContract?.propertyAddress) {
    normalizeAddress(contract.renewalContract.propertyAddress);
  }
  if (contract.mailingAddress) {
    normalizeAddress(contract.mailingAddress);
  }
  if (contract.billingAddress) {
    normalizeAddress(contract.billingAddress);
  }
  return contract;
}